import React from 'react';
import {createBrowserRouter, type RouteObject, RouterProvider} from 'react-router-dom';
import {Home} from './pages/home';
import {Download} from './pages/download';
import {Experiment} from './pages/experiment';
import {Job} from './pages/job';
import {ParameterSet} from './pages/parameterset';
import {Status} from './pages/status';
import {Workspace} from './pages/workspace';
import {Router} from '@remix-run/router/dist/router';
import {Error404} from './common/404';
import {Me} from './pages/me';
import {Configuration} from './pages/config';
import {Frame} from './common/frame';
import {Option} from './pages/option';
import {SwwcButton, SwwcModalDialog, SwwcToast} from '@com.siemens.plm.horizon/swwc/bindings/react';
import type {ButtonVariants} from '@com.siemens.plm.horizon/swwc/dist/types/components/swwc-button/src/_types';

interface ToastItem {
  title: string;
  subTitle?: string | undefined;
  description: string;
  type: 'error' | 'warning' | 'primary';
  opened: boolean;
}

interface AppState {
  toast?: ToastItem | undefined;
  modal?: ModalItem | undefined;
}

interface ModalItem {
  title: string;
  description: string;
  type: 'input' | 'danger';
  opened: boolean;
  okButton: string;
  okVariant: ButtonVariants;
  noButton: string;
  noVariant: ButtonVariants;
  onAction?: ((this: void, action?: 'yes' | 'no' | 'close' | undefined) => void) | undefined;
}

export class App extends React.Component<{}, AppState> {
  static #cachedRouter: Router | undefined;
  static #toastQueue: Array<ToastItem> = [];
  static #modalQueue: Array<ModalItem> = [];
  static instance: App | undefined;

  constructor(props: {}, state: AppState) {
    super(props, state);
    this.state = {};
    App.instance = this;
  }

  static get #router(): Router {
    if (this.#cachedRouter !== undefined) return this.#cachedRouter;
    const routes: Array<RouteObject> = [
      {
        index: true,
        element: <Home />,
        errorElement: <Error404 />,
      },
    ];
    for (const route of [
      {
        paths: ['/dl', '/download', '/downloads', '/file', '/files', '/public', '/publics'],
        element: <Download />,
      },
      {
        paths: [
          '/exp',
          '/exp/:id',
          '/exp/:id/:action',
          '/experiment',
          '/experiment/:id',
          '/experiment/:id/:action',
          '/experiments',
          '/experiments/:id',
          '/experiments/:id/:action',
        ],
        element: <Experiment />,
      },
      {
        paths: ['/job', '/job/:id', '/job/:id/:action', '/jobs', '/jobs/:id', '/jobs/:id/:action'],
        element: <Job />,
      },
      {
        paths: [
          '/pms',
          '/pms/:id',
          '/pms/:id/:action',
          '/parameter',
          '/parameter/:id',
          '/parameter/:id/:action',
          '/parameters',
          '/parameters/:id',
          '/parameters/:id/:action',
          '/parameterset',
          '/parameterset/:id',
          '/parameterset/:id/:action',
          '/parametersets',
          '/parametersets/:id',
          '/parametersets/:id/:action',
        ],
        element: <ParameterSet />,
      },
      {
        paths: [
          '/check',
          '/checks',
          '/health',
          '/healths',
          '/healthcheck',
          '/healthchecks',
          '/stat',
          '/stats',
          '/status',
          '/statuses',
        ],
        element: <Status />,
      },
      {
        paths: ['/opt', '/opts', '/option', '/options'],
        element: <Option />,
      },
      {
        paths: ['/cfg', '/cfgs', '/config', '/configs', '/configuration', '/configurations'],
        element: <Configuration />,
      },
      {
        paths: ['/info', '/infos', '/information', '/informations', '/me'],
        element: <Me />,
      },
      {
        paths: [
          '/ws',
          '/ws/:id',
          '/ws/:id/:action',
          '/workspace',
          '/workspace/:id',
          '/workspace/:id/:action',
          '/workspaces',
          '/workspaces/:id',
          '/workspaces/:id/:action',
        ],
        element: <Workspace />,
      },
    ])
      for (const path of route.paths) routes.push({path, element: route.element});
    this.#cachedRouter = createBrowserRouter(routes);
    return this.#cachedRouter;
  }

  static navigate(path: string) {
    return App.#router.navigate(path, {replace: true});
  }

  static modal(
    type: 'info' | 'error',
    title: string,
    message: string,
    onAction?: (this: void, action?: 'yes' | 'no' | 'close' | undefined) => void,
    okButton: string = 'Yes',
    noButton: string = 'No',
  ): void {
    this.#modalQueue.push({
      type: type === 'info' ? 'input' : 'danger',
      title,
      description: message,
      onAction,
      okButton,
      okVariant: type === 'info' ? 'accent-positive' : 'accent-caution',
      noButton,
      noVariant: type === 'info' ? 'base' : 'positive',
      opened: true,
    });
    if (App.instance) App.instance.#processModal();
  }

  #processModal(action?: boolean | 'yes' | 'no'): void {
    if (this.state.modal !== undefined) {
      if (action !== undefined && action !== true) this.state.modal.opened = false;
      // NOTE: If current toast is visible we skip processing since there should be only 1 modal at any point in time
      if (this.state.modal.opened) return;
      if (this.state.modal.onAction !== undefined) {
        this.state.modal.onAction(action === false ? 'close' : action === 'yes' ? 'yes' : 'no');
        this.state.modal.onAction = undefined;
      }
    }
    this.setState({modal: App.#modalQueue.shift()});
  }

  static toast(type: 'info' | 'warn' | 'error', title: string, subTitle: string | undefined, message: string): void {
    this.#toastQueue.push({
      type: type === 'info' ? 'primary' : type === 'warn' ? 'warning' : 'error',
      title,
      subTitle,
      description: message,
      opened: true,
    });
    if (App.instance) App.instance.#processToast();
  }

  #processToast(opened?: boolean): void {
    if (this.state.toast !== undefined) {
      if (opened === false) this.state.toast.opened = false;
      // NOTE: If current toast is visible we skip processing since the Siemens UX guideline is that only 1 toast at any point in time
      if (this.state.toast.opened) return;
    }
    this.setState({toast: App.#toastQueue.shift()});
  }

  static get pageName() {
    const page = App.#router.state.matches.at(0);
    if (App.#router.state.matches.length > 1) console.debug('Multiple page params detected');
    return page === undefined ? undefined : page.pathnameBase.split('/').at(1);
  }

  static get pageSelection() {
    const page = App.#router.state.matches.at(0);
    if (App.#router.state.matches.length > 1) console.debug('Multiple page params detected');
    return page === undefined ? undefined : page.pathnameBase.split('/').at(2);
  }

  static get pageAction() {
    const page = App.#router.state.matches.at(0);
    if (App.#router.state.matches.length > 1) console.debug('Multiple page params detected');
    return page === undefined ? undefined : page.pathnameBase.split('/').at(3);
  }

  static get pageParam() {
    const page = App.#router.state.matches.at(0);
    if (App.#router.state.matches.length > 1) console.debug('Multiple page params detected');
    return page === undefined ? {} : page.params;
  }

  override render() {
    return (
      <Frame>
        <RouterProvider router={App.#router} />
        {this.state.toast && (
          <SwwcToast
            toastTitle={this.state.toast.title}
            subDescription={this.state.toast.subTitle!}
            description={this.state.toast.description}
            variant={this.state.toast.type}
            opened={this.state.toast.opened}
            onOpenedChanged={(event) => this.#processToast(event.detail.opened)}
          />
        )}
        {this.state.modal && (
          <SwwcModalDialog
            dialogTitle={this.state.modal.title}
            type={this.state.modal.type}
            opened={this.state.modal.opened}
            isModal={true}
            onOpenedChange={(event) => this.#processModal(event.detail.opened)}
          >
            <div>{this.state.modal.description}</div>
            <div
              className='actions'
              slot='actions'
            >
              <SwwcButton
                label={this.state.modal.okButton}
                onClickEvent={() => this.#processModal('yes')}
                variant='accent-caution'
              />
              <SwwcButton
                label={this.state.modal.noButton}
                onClickEvent={() => this.#processModal('no')}
              />
            </div>
          </SwwcModalDialog>
        )}
      </Frame>
    );
  }
}

export default App;
